/**
 * PROJECT STYLE phase 2 — WINDOWS (2026-10-06, Matt: "do we generally want windows and doors to
 * have muntins or be more clean? … 'Modern' … full aluminum doors and windows with no muntins …
 * 'Transitional Modern' maybe has muntins in horizontals only and windows and doors default to
 * aluminum clad which means they have an interior and exterior color setting"). Plan + decisions:
 * docs/bim-plan/23-project-style.md.
 *
 * The project's OPENING STYLE — one record in the settings map (key 'openingStyle'), edited in
 * Project Settings → Windows & Doors. Every field is optional: unset = the pre-round behavior, so a
 * model nobody styled builds byte-identical.
 *
 * LIVE inheritance (Matt, Q5): an opening's style field resolves
 *   instance → type (only when the TYPE record stores it) → project style → built-in default
 * and doc.ts writes the EFFECTIVE value into the resolved params, so every consumer (3D, plan,
 * elevations, schedules) keeps reading plain `params.x`. `resolved.styleFollows` records which
 * fields came from the project, for the properties UI.
 */
import type { ParamValue } from './types'
import type { FrameMount } from './openingFrame'
import { isHardwareLook, isDoorHardwareFinish } from './doorHardware'

export type FrameSystem = 'aluminum' | 'clad' | 'wood' | 'steel' | 'fiberglass'

export const FRAME_SYSTEMS: { value: FrameSystem; label: string; twoColor: boolean; hint: string }[] = [
  { value: 'aluminum', label: 'Aluminum', twoColor: false, hint: 'One color, inside and out.' },
  { value: 'clad', label: 'Aluminum-Clad Wood', twoColor: true, hint: 'An exterior color on the aluminum, an interior color (or the natural wood) inside.' },
  { value: 'wood', label: 'Wood', twoColor: true, hint: 'Painted or stained, each side its own.' },
  { value: 'steel', label: 'Steel', twoColor: false, hint: 'One color, narrow sightlines and muntins.' },
  { value: 'fiberglass', label: 'Fiberglass', twoColor: false, hint: 'One color, inside and out.' },
]
export const isFrameSystem = (v: unknown): v is FrameSystem => FRAME_SYSTEMS.some((f) => f.value === v)
export const frameSystemLabel = (v: unknown): string => FRAME_SYSTEMS.find((f) => f.value === v)?.label ?? ''
export const frameSystemTwoColor = (v: unknown): boolean => FRAME_SYSTEMS.find((f) => f.value === v)?.twoColor === true

export type MuntinPatternKey = 'none' | 'grid' | 'horizontal' | 'prairie' | 'topSash'

export const MUNTIN_PATTERNS: { value: MuntinPatternKey; label: string; rows: boolean; cols: boolean }[] = [
  { value: 'none', label: 'None', rows: false, cols: false },
  { value: 'grid', label: 'Grid', rows: true, cols: true },
  { value: 'horizontal', label: 'Horizontal Only', rows: true, cols: false },
  { value: 'prairie', label: 'Prairie', rows: false, cols: false },
  { value: 'topSash', label: 'Top Sash Only', rows: true, cols: true },
]
export const isMuntinPattern = (v: unknown): v is MuntinPatternKey => MUNTIN_PATTERNS.some((m) => m.value === v)

/**
 * What a muntin pattern BUILDS — the one reading every consumer shares (3D bars, elevation
 * linework, the schedule's DIV. LITES):
 *   grid       rows x cols across the lite
 *   horizontal rows x 1
 *   prairie    a border of bars inset from each edge (two across, two up)
 *   topSash    a grid on the UPPER sash of a hung window only (any other window: the whole lite)
 */
export interface MuntinLayout { kind: 'grid' | 'prairie'; rows: number; cols: number; topOnly: boolean }

export function muntinLayout(pattern: ParamValue | undefined, rows: ParamValue | undefined, cols: ParamValue | undefined): MuntinLayout | null {
  const r = Math.max(1, Math.round((rows as number) || 2))
  const c = Math.max(1, Math.round((cols as number) || 2))
  switch (pattern) {
    case 'grid': return { kind: 'grid', rows: r, cols: c, topOnly: false }
    case 'horizontal': return { kind: 'grid', rows: r, cols: 1, topOnly: false }
    case 'topSash': return { kind: 'grid', rows: r, cols: c, topOnly: true }
    case 'prairie': return { kind: 'prairie', rows: 3, cols: 3, topOnly: false }
    default: return null
  }
}

/** prairie bars sit this far in from each glass edge — 4", never more than a quarter of the lite */
export const prairieInset = (spanFt: number): number => Math.min(1 / 3, spanFt / 4)

/** the schedule's DIV. LITES text ('' = none) */
export function muntinLabel(pattern: ParamValue | undefined, rows: ParamValue | undefined, cols: ParamValue | undefined): string {
  const m = muntinLayout(pattern, rows, cols)
  if (!m) return ''
  if (m.kind === 'prairie') return 'Prairie'
  return `${m.rows} x ${m.cols}${m.topOnly ? ' top sash' : ''}`
}

const MOUNTS: FrameMount[] = ['exterior', 'centered', 'interior']
const isMount = (v: unknown): v is FrameMount => MOUNTS.includes(v as FrameMount)

export interface OpeningStyleSettings {
  frameSystem: FrameSystem | null
  muntinPattern: MuntinPatternKey | null
  muntinRows: number
  muntinCols: number
  frameMount: FrameMount | null
  /** exterior-mounted units: how far the frame sits back from the exterior finish face
   *  (a deep stucco return); null = flush to the sheathing, as before */
  recessFt: number | null
  /** an exterior trim style key (exteriorTrim.ts), or null = the type's own */
  exteriorTrim: string | null
  /** PROJECT STYLE phase 3: each door condition's defaults (absent = the door type's own look) */
  doorConditions: Partial<Record<DoorCondition, DoorConditionStyle>>
  /** TYPICAL DOOR HEIGHTS (2026-10-08, Matt): the project's door height in interior / exterior
   *  walls — every door without a height of its own follows it (null = the type's first size) */
  interiorDoorHeightFt: number | null
  exteriorDoorHeightFt: number | null
  /** new windows are placed with their head lined up to the door height of their wall */
  windowHeadsAlign: boolean
  /** DOOR HARDWARE round: the project's hardware look + finish (doorHardware.ts; null = by construction) */
  hardwareLook: string | null
  hardwareFinish: string | null
}

export const DEFAULT_OPENING_STYLE: OpeningStyleSettings = {
  frameSystem: null, muntinPattern: null, muntinRows: 2, muntinCols: 2, frameMount: null, recessFt: null, exteriorTrim: null,
  doorConditions: {},
  interiorDoorHeightFt: null, exteriorDoorHeightFt: null, windowHeadsAlign: false,
  hardwareLook: null, hardwareFinish: null,
}

const doorHeightOrNull = (v: unknown): number | null =>
  typeof v === 'number' && isFinite(v) && v >= 5 && v <= 16 ? v : null

// ---------- TYPICAL DOOR HEIGHTS (2026-10-08) ----------
//
// Matt: "add to the Project Settings under Doors and Windows the typical interior door height."
// A door's height follows the project's typical height for its wall (interior / exterior) unless
// the door has a height of its own — the same live inheritance as every other style field. A door
// is placed WITHOUT a stored height (commands/elements.ts), so it follows from the start; picking a
// specific height in the size picker gives it its own. Garage doors, gates and site walls never
// follow (their heights are their own product).

/** doors that follow the project's typical height */
export function doorFollowsProjectHeight(operation: unknown): boolean {
  const op = String(operation || 'hinged')
  return !['sectional', 'schweiss', 'carriage', 'gateSwing', 'gateSlide'].includes(op)
}

/** the project's typical door height for a wall (null = none set) */
export function projectDoorHeightFt(s: OpeningStyleSettings, hostExterior: boolean): number | null {
  return hostExterior ? s.exteriorDoorHeightFt : s.interiorDoorHeightFt
}

export function normalizeOpeningStyle(raw: unknown): OpeningStyleSettings {
  const r = (raw || {}) as Partial<OpeningStyleSettings>
  const int = (v: unknown, d: number) => (typeof v === 'number' && isFinite(v) ? Math.max(1, Math.min(12, Math.round(v))) : d)
  return {
    frameSystem: isFrameSystem(r.frameSystem) ? r.frameSystem : null,
    muntinPattern: isMuntinPattern(r.muntinPattern) ? r.muntinPattern : null,
    muntinRows: int(r.muntinRows, 2),
    muntinCols: int(r.muntinCols, 2),
    frameMount: isMount(r.frameMount) ? r.frameMount : null,
    recessFt: typeof r.recessFt === 'number' && isFinite(r.recessFt) && r.recessFt >= 0 ? Math.min(r.recessFt, 2) : null,
    exteriorTrim: typeof r.exteriorTrim === 'string' && r.exteriorTrim ? r.exteriorTrim : null,
    doorConditions: normalizeDoorConditions(r.doorConditions),
    interiorDoorHeightFt: doorHeightOrNull(r.interiorDoorHeightFt),
    exteriorDoorHeightFt: doorHeightOrNull(r.exteriorDoorHeightFt),
    windowHeadsAlign: r.windowHeadsAlign === true,
    hardwareLook: isHardwareLook(r.hardwareLook) ? r.hardwareLook : null,
    hardwareFinish: isDoorHardwareFinish(r.hardwareFinish) ? r.hardwareFinish : null,
  }
}

/** which style fields an opening took from the project (for the properties UI) */
export interface StyleFollows { hardwareLook?: boolean; doorHeight?: boolean; frameSystem?: boolean; muntins?: boolean; frameMount?: boolean; recess?: boolean; exteriorTrim?: boolean; condition?: boolean; panelStyle?: boolean; interiorJamb?: boolean }

const blank = (v: unknown) => v === undefined || v === null || v === ''

/**
 * Fold the project opening style into one door/window's resolved params, in place. `inst` is
 * the element's STORED instance params, `typeRaw` the type record's STORED params (never the
 * schema-filled ones — a schema default is not the type "setting" something).
 */
export function applyOpeningStyle(
  category: string,
  params: Record<string, ParamValue>,
  inst: Record<string, ParamValue>,
  typeRaw: Record<string, ParamValue>,
  s: OpeningStyleSettings,
  /** the host wall is an EXTERIOR wall — the project's mount, recess, trim and colors govern
   *  openings in exterior walls only (an interior door keeps its type's look) */
  hostExterior: boolean,
): StyleFollows {
  const f: StyleFollows = {}
  // frame system: instance → type → project → none. The project's frame system is the EXTERIOR
  // window & door system (Matt, 2026-10-08: "I set the windows and doors to aluminum, but I meant
  // that for the exterior elements, not the interior doors") — an opening in an interior wall
  // keeps its own or its type's.
  const projectFs = hostExterior ? s.frameSystem : null
  const fs = !blank(inst.frameSystem) ? inst.frameSystem : !blank(typeRaw.frameSystem) ? typeRaw.frameSystem : projectFs
  params.frameSystem = isFrameSystem(fs) ? fs : ''
  if (blank(inst.frameSystem) && blank(typeRaw.frameSystem) && projectFs) f.frameSystem = true
  // frame mount: instance → type (a stored exterior/centered/interior; 'auto' or blank defer) →
  // project → resolveFrameMount's own rule. Nearly every firm type stores 'auto' (measured
  // 2026-10-06: 37 of 41), which is what lets the project's setting position reach them.
  const typeMount = typeRaw.frameMountDefault
  if (hostExterior && !isMount(inst.frameMount) && (blank(typeMount) || typeMount === 'auto') && s.frameMount) {
    params.frameMount = s.frameMount
    f.frameMount = true
  }
  // recess (exterior mount only — read by openingFrameFaces' callers)
  if (hostExterior && blank(inst.frameRecessFt) && s.recessFt != null) { params.frameRecessFt = s.recessFt; f.recess = true }
  // exterior trim: instance → type → project
  if (hostExterior && blank(inst.exteriorTrim) && blank(typeRaw.exteriorTrimStyle) && s.exteriorTrim) {
    params.exteriorTrim = s.exteriorTrim
    f.exteriorTrim = true
  }
  // muntins (windows): a blank instance pattern follows the project
  if (category === 'window' && blank(inst.muntinPattern)) {
    if (s.muntinPattern) {
      params.muntinPattern = s.muntinPattern
      params.muntinRows = s.muntinRows
      params.muntinCols = s.muntinCols
      f.muntins = true
    } else params.muntinPattern = 'none'
  }
  return f
}

// ---------- PROJECT STYLE phase 3 — DOOR CONDITIONS + PANEL STYLES (2026-10-07) ----------
//
// Matt (plan Q3): "standard door panel styles for each condition? Interior doors, exterior patio
// doors, french doors, etc. That then set the default look of the project throughout unless
// specifically overridden at the instance!" Timeless panel styles only (no raised / 6-panel /
// louvered). An arched top is the opening's Head Type, not a panel style.

export type DoorCondition =
  | 'entry' | 'patio' | 'exteriorFrench' | 'passage' | 'interiorFrench' | 'closet' | 'garage' | 'utility'

export const DOOR_CONDITIONS: { value: DoorCondition; label: string; hint: string }[] = [
  { value: 'entry', label: 'Exterior Entry', hint: 'Hinged doors in exterior walls. The Front Door color goes on the door itself.' },
  { value: 'patio', label: 'Exterior Patio', hint: 'Sliders, multi-slides and folding glass walls in exterior walls.' },
  { value: 'exteriorFrench', label: 'Exterior Double', hint: 'Double hinged pairs in exterior walls.' },
  { value: 'passage', label: 'Interior Passage', hint: 'Hinged and pocket doors in interior walls.' },
  { value: 'interiorFrench', label: 'Interior Double', hint: 'Double hinged pairs in interior walls.' },
  { value: 'closet', label: 'Closet', hint: 'Bifolds and bypass sliders in interior walls.' },
  { value: 'garage', label: 'Garage', hint: 'Overhead, upward-folding and carriage doors.' },
  { value: 'utility', label: 'Utility / Service', hint: 'Set on a door by hand (a garage side door, a mechanical room).' },
]
export const isDoorCondition = (v: unknown): v is DoorCondition => DOOR_CONDITIONS.some((c) => c.value === v)
export const doorConditionLabel = (v: unknown): string => DOOR_CONDITIONS.find((c) => c.value === v)?.label ?? ''

export type DoorPanelStyle =
  | 'flush' | 'shaker1' | 'shaker2' | 'shaker3' | 'shaker5' | 'raised1' | 'raised2' | 'raised3' | 'raised5'
  | 'plank' | 'fullLite' | 'halfLite' | 'frenchLite'

export const DOOR_PANEL_STYLES: { value: DoorPanelStyle; label: string; glazed: boolean }[] = [
  { value: 'flush', label: 'Flat (Flush)', glazed: false },
  { value: 'shaker1', label: 'Shaker, 1 Panel', glazed: false },
  { value: 'shaker2', label: 'Shaker, 2 Panel', glazed: false },
  { value: 'shaker3', label: 'Shaker, 3 Panel', glazed: false },
  { value: 'shaker5', label: 'Shaker, 5 Panel', glazed: false },
  // DOOR LEAF round (2026-10-07, Matt: "solid flat panel, solid raised panel"): the raised field
  // sits proud of the panel inside the stiles and rails (leafLayout.ts)
  { value: 'raised1', label: 'Raised, 1 Panel', glazed: false },
  { value: 'raised2', label: 'Raised, 2 Panel', glazed: false },
  { value: 'raised3', label: 'Raised, 3 Panel', glazed: false },
  { value: 'raised5', label: 'Raised, 5 Panel', glazed: false },
  { value: 'plank', label: 'Plank (Vertical Boards)', glazed: false },
  { value: 'halfLite', label: 'Half-Lite', glazed: true },
  { value: 'fullLite', label: 'Full-Lite', glazed: true },
  { value: 'frenchLite', label: 'Divided-Lite French', glazed: true },
]
export const isDoorPanelStyle = (v: unknown): v is DoorPanelStyle => DOOR_PANEL_STYLES.some((p) => p.value === v)
export const doorPanelStyleLabel = (v: unknown): string => DOOR_PANEL_STYLES.find((p) => p.value === v)?.label ?? ''
export const doorPanelGlazed = (v: unknown): boolean => DOOR_PANEL_STYLES.find((p) => p.value === v)?.glazed === true

/** operations whose leaves never take a panel style (glass walls, garage sections, gates, no door) */
const PANEL_FREE_OPS = ['multiSlide', 'multiBifold', 'cornerSlider', 'sectional', 'schweiss', 'casedOpening', 'gateSwing', 'gateSlide']
export const doorTakesPanelStyle = (operation: unknown): boolean => !PANEL_FREE_OPS.includes(String(operation || ''))
/** DOOR LEAF round: the panel-free operations that are glass walls (the rest are solid) */
const PANEL_FREE_GLASS_OPS = ['multiSlide', 'multiBifold', 'cornerSlider']
/** DOOR LEAF round: operations whose leaves are glass unless a door says otherwise (a patio slider) */
const GLASS_DEFAULT_OPS = ['slider']
/** the panel style a door builds when nothing — the door, the project — sets one */
export const builtInPanelStyle = (operation: unknown): DoorPanelStyle =>
  GLASS_DEFAULT_OPS.includes(String(operation || '')) ? 'fullLite' : 'flush'

/**
 * A door's condition, inferred when it isn't set: what it is (its operation) and where it is (an
 * exterior or an interior wall). Gates and cased openings have none.
 */
export function inferDoorCondition(operation: unknown, hostExterior: boolean): DoorCondition | null {
  const op = String(operation || 'hinged')
  if (op === 'gateSwing' || op === 'gateSlide' || op === 'casedOpening') return null
  if (op === 'sectional' || op === 'schweiss' || op === 'carriage') return 'garage'
  if (hostExterior) {
    if (op === 'slider' || op === 'multiSlide' || op === 'multiBifold' || op === 'cornerSlider' || op === 'mirrorSlider') return 'patio'
    if (op === 'french') return 'exteriorFrench'
    return 'entry'
  }
  if (op === 'french') return 'interiorFrench'
  if (op === 'bifold' || op === 'mirrorSlider' || op === 'slider') return 'closet'
  return 'passage'
}

/** one condition's project defaults (every field optional: unset = the door type's own look) */
export interface DoorConditionStyle {
  panelStyle: DoorPanelStyle | null
  /** muntins on a glazed panel style (a French door's divided lites) */
  muntinPattern: MuntinPatternKey | null
  muntinRows: number
  muntinCols: number
  /** PROJECT STYLE phase 5: the condition's hardware set (a firm Hardware catalog set name) — the
   *  door schedule's HDWR GRP — and the handle that set implies (mapped at pick time, HANDLE_MAP) */
  hardwareSet?: string | null
  hardwareHandle?: DoorHandle | null
}
export type DoorHandle = 'lever' | 'knob' | 'ladderPull' | 'none'
const DOOR_HANDLES: readonly DoorHandle[] = ['lever', 'knob', 'ladderPull', 'none']
export const DEFAULT_DOOR_CONDITION_STYLE: DoorConditionStyle = { panelStyle: null, muntinPattern: null, muntinRows: 5, muntinCols: 3, hardwareSet: null, hardwareHandle: null }

export function normalizeDoorConditions(raw: unknown): Partial<Record<DoorCondition, DoorConditionStyle>> {
  const out: Partial<Record<DoorCondition, DoorConditionStyle>> = {}
  const src = (raw || {}) as Record<string, Partial<DoorConditionStyle>>
  const int = (v: unknown, d: number) => (typeof v === 'number' && isFinite(v) ? Math.max(1, Math.min(12, Math.round(v))) : d)
  for (const c of DOOR_CONDITIONS) {
    const r = src[c.value]
    if (!r) continue
    const s: DoorConditionStyle = {
      panelStyle: isDoorPanelStyle(r.panelStyle) ? r.panelStyle : null,
      muntinPattern: isMuntinPattern(r.muntinPattern) ? r.muntinPattern : null,
      muntinRows: int(r.muntinRows, 5),
      muntinCols: int(r.muntinCols, 3),
      hardwareSet: typeof r.hardwareSet === 'string' && r.hardwareSet.trim() ? r.hardwareSet.trim() : null,
      hardwareHandle: (DOOR_HANDLES as readonly string[]).includes(r.hardwareHandle as string) ? (r.hardwareHandle as DoorHandle) : null,
    }
    if (s.panelStyle || s.muntinPattern || s.hardwareSet) out[c.value] = s
  }
  return out
}

/**
 * Fold a door's condition + panel style into its resolved params (in place) — the same live
 * inheritance as the rest of the style: instance → type (only when stored) → the project's
 * setting for the door's condition → the type's own glazing (a glazed type is Full-Lite, a solid
 * one Flush). A GLAZED type (a glass slider) stays glass: a project style for its condition only
 * reaches it when that style is glazed too.
 */
export function applyDoorStyle(
  params: Record<string, ParamValue>,
  inst: Record<string, ParamValue>,
  typeRaw: Record<string, ParamValue>,
  typeParams: Record<string, ParamValue>,
  conditions: Partial<Record<DoorCondition, DoorConditionStyle>>,
  hostExterior: boolean,
  typicalHeights?: { interior: number | null; exterior: number | null },
): { condition?: boolean; panelStyle?: boolean; muntins?: boolean; hardware?: boolean; doorHeight?: boolean } {
  const f: { condition?: boolean; panelStyle?: boolean; muntins?: boolean; hardware?: boolean; doorHeight?: boolean } = {}
  const op = typeParams.operation
  // TYPICAL DOOR HEIGHTS: no height of its own → the project's for its wall, else the type's
  // first standard size (what a door was placed at before this round)
  if (blank(inst.heightFt) && doorFollowsProjectHeight(op)) {
    const h = typicalHeights ? (hostExterior ? typicalHeights.exterior : typicalHeights.interior) : null
    if (h) { params.heightFt = h; f.doorHeight = true }
    else {
      const first = Array.isArray(typeParams.sizeSeries) ? (typeParams.sizeSeries[0] as { heightFt?: number } | undefined) : undefined
      if (first && typeof first.heightFt === 'number') params.heightFt = first.heightFt
    }
    params.__heightFollows = true
  }
  const cond = isDoorCondition(inst.doorCondition) ? inst.doorCondition : inferDoorCondition(op, hostExterior)
  params.doorCondition = cond ?? ''
  if (!isDoorCondition(inst.doorCondition) && cond) f.condition = true
  // PROJECT STYLE phase 5: the condition's hardware set (HDWR GRP) and its handles — the door's
  // own set wins; the condition's beats the type's by-name default (it knows where the door is).
  // A closet door gets no handle inside it.
  const csH = cond ? conditions[cond] : undefined
  if (csH?.hardwareSet && blank(inst.hardwareGroup)) {
    params.hardwareGroup = csH.hardwareSet
    f.hardware = true
    if (csH.hardwareHandle) {
      if (blank(inst.handleExterior)) params.handleExterior = csH.hardwareHandle
      if (blank(inst.handleInterior)) params.handleInterior = cond === 'closet' ? 'none' : csH.hardwareHandle
    }
  }
  // DOOR LEAF round (2026-10-07): glass is the DOOR's (its panel style), never its type's.
  // `glazing` stays a resolved param — written here, read by every consumer (3D, plan,
  // section hatch, elevation) exactly as before, and mirrored onto the resolved typeParams
  // by doc.ts so readers of either keep working.
  const setGlazing = (on: boolean) => {
    params.glazing = on ? 'full' : 'none'
    if (!on) params.muntinPattern = 'none'
  }
  if (!doorTakesPanelStyle(op)) {
    params.panelStyle = ''
    params.panelStyleLower = ''
    setGlazing(PANEL_FREE_GLASS_OPS.includes(String(op || '')))
    return f
  }
  void typeRaw
  // DOOR LEAF round: a pivot door's hardware is a ladder pull, both sides, until a door says otherwise
  if (op === 'pivot') {
    if (blank(inst.handleExterior)) params.handleExterior = 'ladderPull'
    if (blank(inst.handleInterior)) params.handleInterior = 'ladderPull'
  }
  const glassDefault = GLASS_DEFAULT_OPS.includes(String(op || ''))
  const builtIn: DoorPanelStyle = builtInPanelStyle(op)
  const cs = cond ? conditions[cond] : undefined
  let style: DoorPanelStyle
  if (isDoorPanelStyle(inst.panelStyle)) style = inst.panelStyle
  // a glass-by-default slider only takes the project's style for its condition when that's glass too
  else if (cs?.panelStyle && (!glassDefault || doorPanelGlazed(cs.panelStyle))) { style = cs.panelStyle; f.panelStyle = true }
  else style = builtIn
  params.panelStyle = style
  // DUTCH LOWER LEAF (2026-10-12, Matt: "a glazed top and a solid bottom"): a Dutch door's lower
  // leaf takes its own panel style; blank = the same as the upper (panelStyle above)
  const lower: DoorPanelStyle | '' = op === 'dutch' ? (isDoorPanelStyle(inst.panelStyleLower) ? inst.panelStyleLower : style) : ''
  params.panelStyleLower = lower
  const anyGlazed = doorPanelGlazed(style) || doorPanelGlazed(lower)
  // muntins on a glazed style: instance → the condition's → a French door's own 5 x 3
  if (anyGlazed && blank(inst.muntinPattern)) {
    if (cs?.muntinPattern) {
      params.muntinPattern = cs.muntinPattern
      params.muntinRows = cs.muntinRows
      params.muntinCols = cs.muntinCols
      f.muntins = true
    } else if (style === 'frenchLite' || lower === 'frenchLite') {
      params.muntinPattern = 'grid'
      params.muntinRows = 5
      params.muntinCols = 3
    }
  }
  setGlazing(anyGlazed)
  return f
}
